package ru.villawonka.app.presentation.screens.search.compose.layout

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.painterResource
import ru.villawonka.app.getTelegram
import ru.villawonka.app.platform.telegram.model.toDp
import ru.villawonka.app.presentation.design.components.AppButton
import ru.villawonka.app.presentation.design.components.AppButtonSize
import ru.villawonka.app.presentation.design.components.AppButtonType
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.onSurfaceSecondary
import ru.villawonka.app.presentation.screens.search.compose.SearchCallbacks
import ru.villawonka.app.presentation.screens.search.compose.view.SearchTopBar
import ru.villawonka.app.presentation.screens.search.compose.view.TopBarView
import ru.villawonka.app.presentation.screens.search.viewmodel.SearchResultsUIState
import ru.villawonka.app.presentation.screens.search.viewmodel.SearchUIEvent
import ru.villawonka.app.presentation.screens.search.viewmodel.SearchUIState
import ru.villawonka.app.presentation.screens.search.viewmodel.SearchViewModel
import ru.villawonka.app.presentation.sharedviews.DefaultErrorStateLayout
import ru.villawonka.app.presentation.sharedviews.DefaultLoadingStateLayout
import ru.villawonka.app.presentation.sharedviews.postList.compose.PostListView
import ru.villawonka.app.resources.AppResources
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.stub_no_search_results

@Composable
fun SearchDataStateLayout(
    viewModel: SearchViewModel,
    searchState: SearchUIState.Data,
    searchResultsState: SearchResultsUIState,
    callbacks: SearchCallbacks,
) {

    val density = LocalDensity.current.density
    val listState = rememberLazyListState()
    val bottomInset = getTelegram()?.getSafeAreaInset()?.bottom?.toDp(density) ?: 0.dp
    val bottomNavigationHeight = 56.dp
    var topBarHeight by remember { mutableStateOf(0.dp) }

    LaunchedEffect(viewModel) {
        viewModel.searchUIEvents.collect { event ->
            when (event) {
                is SearchUIEvent.ScrollToUp -> listState.scrollToItem(0)
                else -> Unit
            }
        }
    }

    Box {
        when (searchResultsState) {
            is SearchResultsUIState.Loading -> {
                DefaultLoadingStateLayout(
                    topPadding = topBarHeight,
                )
            }
            is SearchResultsUIState.Error -> {
                DefaultErrorStateLayout(
                    topPadding = topBarHeight,
                    failure = searchResultsState.failure,
                    onRetryClick = callbacks.onRetryButtonClick
                )
            }
            is SearchResultsUIState.Empty -> {
                SearchResultEmptyDataStub(
                    callbacks = callbacks,
                    topPadding = topBarHeight,
                    bottomPadding = bottomNavigationHeight
                )
            }
            is SearchResultsUIState.Data -> {
                PostListView(
                    topPadding = topBarHeight,
                    bottomPadding = bottomNavigationHeight + bottomInset,
                    title = AppResources.strings.searchResultTitle,
                    posts = searchResultsState.posts,
                    onLoadSimilar = callbacks.onLoadSimilar,
                    onLoadNextPage = callbacks.onLoadNextPage,
                    hasMoreData = searchResultsState.hasMoreData,
                    listState = listState
                )
            }
        }
    }

    Column(
        Modifier.onGloballyPositioned {
            val heightPx = it.size.height
            topBarHeight = heightPx.toDp(density)
        }
    ) {
        SearchTopBar(
            country = searchState.country,
            searchCategories = searchState.allSearchCategories,
            listState = listState,
            onTabSelect = { callbacks.onTabSelected(it) },
            onStartButtonClick = callbacks.onMapPinButtonClick,
            onEndButtonClick = callbacks.onSettingsButtonClick,
            selectedSearchCategory = searchState.selectedSearchCategory,
        )
        TopBarView(
            onSearchFilterClick = callbacks.onSearchFilterButtonClick,
            onSearchSortClick = callbacks.onSearchSortButtonClick,
            sortType = searchState.selectedSortType,
            hasCustomFilters = searchState.hasCustomFilters,
            listState = listState,
        )
    }
}

@Composable
fun SearchResultEmptyDataStub(callbacks: SearchCallbacks, topPadding: Dp, bottomPadding: Dp) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(
                top = topPadding,
                bottom = bottomPadding
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Image(
                painter = painterResource(Res.drawable.stub_no_search_results),
                contentDescription = null,
                modifier = Modifier.width(width = 300.dp)
            )
            AppSpacer(8.dp)
            Text(
                text = AppResources.strings.searchResultEmptyLabel,
                style = AppTextStyles.bodyMediumMedium,
                color = MaterialTheme.colors.onSurfaceSecondary,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(horizontal = 27.dp)
            )
            AppSpacer(16.dp)
            AppButton(
                text = AppResources.strings.searchResultAdjustFiltersButton,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM,
                modifier = Modifier.width(260.dp)
            ) {
                callbacks.onSearchFilterButtonClick()
            }
        }
    }
}